import React, { useState } from 'react';
import { Button, Card, Form, Input, Result, Spin } from 'antd';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { Link, useSearchParams } from 'react-router-dom';
import { postApi } from '../../redux/rest/restSlice';
import { RESET_PASSWORD } from '../../constants/actionTypes';
import { FORGOT_PASSWORD, LOGIN } from '../../constants/routes';
import { LOGO_URL, BRAND_NAME } from '../../constants/brand';
import { useDocumentTitle } from '../../components/PageHeader';

const MIN_PASSWORD_LENGTH = 8;

const ResetPasswordScreen = () => {
    const [loading, setLoading] = useState(false);
    const [isReset, setIsReset] = useState(false);
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const dispatch = useDispatch();
    useDocumentTitle('Reset Password');

    const onFinish = ({ password }) => {
        setLoading(true);
        dispatch(postApi({ endpoint: RESET_PASSWORD, payload: { token, password } }))
            .then(unwrapResult)
            .then(() => setIsReset(true))
            .catch(() => {
                // the axios interceptor already shows the error
            })
            .finally(() => setLoading(false));
    };

    const renderContent = () => {
        if (!token) {
            return (
                <Result
                    status='warning'
                    title='Invalid reset link'
                    subTitle='This link is missing its reset code. Please request a new one.'
                    extra={
                        <Link to={FORGOT_PASSWORD}>
                            <Button type='primary' size='large' className='default-primary-btn'>
                                Request a new link
                            </Button>
                        </Link>
                    }
                />
            );
        }

        if (isReset) {
            return (
                <Result
                    status='success'
                    title='Password reset'
                    subTitle='You can now log in with your new password.'
                    extra={
                        <Link to={LOGIN}>
                            <Button type='primary' size='large' className='default-primary-btn'>
                                Go to login
                            </Button>
                        </Link>
                    }
                />
            );
        }

        return (
            <>
                <div className='heading'>Reset Password</div>
                <div className='login-subheading'>Choose a new password for your account.</div>

                <div className='login-form mt-30'>
                    <Form
                        name='reset-password'
                        layout='vertical'
                        onFinish={onFinish}
                        autoComplete='off'
                        requiredMark={false}
                    >
                        <Form.Item
                            label="New password"
                            name="password"
                            className="form-item"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter a new password'
                                },
                                {
                                    min: MIN_PASSWORD_LENGTH,
                                    message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`
                                }
                            ]}
                        >
                            <Input.Password size='large' placeholder='New password' />
                        </Form.Item>

                        <Form.Item
                            label="Confirm new password"
                            name="confirm_password"
                            className="form-item"
                            dependencies={['password']}
                            rules={[
                                {
                                    required: true,
                                    message: 'Please confirm your new password'
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Passwords do not match'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password size='large' placeholder='Confirm new password' />
                        </Form.Item>

                        <div className="mt-20">
                            <Form.Item>
                                <Button
                                    type="primary"
                                    block
                                    size='large'
                                    htmlType="submit"
                                    className='default-primary-btn'
                                >
                                    Reset password
                                </Button>
                            </Form.Item>
                        </div>
                    </Form>
                </div>

                <div className='login-links'>
                    <Link to={LOGIN}>Back to login</Link>
                </div>
            </>
        );
    };

    return (
        <div className='login-page'>
            <Spin spinning={loading}>
                <Card bordered={false} className='login-card'>
                    <div className='login-container'>
                        <div className='login-brand'>
                            <img src={LOGO_URL} alt='GoGo Power' draggable={false} />
                            <span className='login-brand-text'>{BRAND_NAME}</span>
                        </div>

                        {renderContent()}
                    </div>
                </Card>
            </Spin>
        </div>
    );
};

export default ResetPasswordScreen;
